import type {ComponentPropsWithoutRef, Key} from 'react';
import {useState, useCallback, useRef} from 'react';
import {useOverlayPosition} from '@react-aria/overlays';
import noop from '@tinkoff/utils/function/noop';
import type {CollectionChildren} from '@react-types/shared';

import {MenuItem} from './menu-item';
import {Menu} from './menu';

type Props = ComponentPropsWithoutRef<typeof MenuItem> & {
    subMenuItems: CollectionChildren<object>;
};

export const MenuItemWithSubMenu = ({subMenuItems, ...props}: Props) => {
    const [isSubMenuOpen, setSubMenuOpen] = useState(false);

    const itemRef = useRef<HTMLLIElement>(null);
    const subMenuRef = useRef<HTMLUListElement>(null);

    const handleItemFocus = useCallback(() => setSubMenuOpen(true), []);
    const handleItemBlur = useCallback(() => setSubMenuOpen(false), []);
    const handleSubmenuFocus = useCallback(() => setSubMenuOpen(true), []);
    const handleSubmenuBlur = useCallback(() => setSubMenuOpen(false), []);
    const handleAction = useCallback((key: Key) => props.onAction?.(`${props.item.key}.${key}`), [props]);

    const {overlayProps: positionProps} = useOverlayPosition({
        targetRef: itemRef,
        overlayRef: subMenuRef,
        placement: 'right',
        isOpen: isSubMenuOpen,
    });

    return (
        <>
            <MenuItem
                {...props}
                onClose={noop}
                onAction={noop}
                ref={itemRef}
                onFocus={handleItemFocus}
                onBlur={handleItemBlur}
                hasSubMenu={true}
            />
            <Menu
                role='group'
                tabIndex={undefined}
                style={{...positionProps.style, visibility: isSubMenuOpen ? 'visible' : 'hidden'}}
                ref={subMenuRef}
                onAction={handleAction}
                onClose={props.onClose}
                onFocus={handleSubmenuFocus}
                onBlur={handleSubmenuBlur}
            >
                {subMenuItems}
            </Menu>
        </>
    );
};
